"use client"

import type React from "react"

import { useState, useEffect } from "react"
import Link from "next/link"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { Menu, X, Search, Globe, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ThemeToggle } from "@/components/theme-toggle"
import { cn } from "@/lib/utils"

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const pathname = usePathname()

  const navLinks = [
    { name: "Home", href: "/" },
    {
      name: "Blog",
      href: "/blog",
      dropdown: [
        { name: "All Posts", href: "/blog" },
        { name: "Categories", href: "/categories" },
        { name: "Featured Posts", href: "/blog?featured=true" },
      ],
    },
    { name: "Pekoe Trail", href: "/pekoe-trail" },
    {
      name: "Destinations",
      href: "/destinations",
    },
    { name: "About", href: "/about" },
    { name: "Contact", href: "/contact" },
  ]

  const isActive = (path: string) => {
    if (path === "/") {
      return pathname === path
    }
    return pathname.startsWith(path)
  }

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      window.location.href = `/blog?search=${encodeURIComponent(searchQuery)}`
      setIsSearchOpen(false)
    }
  }

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-all duration-200",
        isScrolled
          ? "border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-sm"
          : "bg-background",
      )}
    >
      <div className="container flex h-16 items-center">
        <div className="mr-4 flex">
          <Link href="/" className="flex items-center space-x-2">
            {/* <Globe className="h-6 w-6 text-primary" /> */}
            <Image
              src="/natouris.png"
              alt="Natouris Logo"
              width={64}
              height={64}
              className="h-15 w-auto"
              priority
            />
            {/* <span className="hidden font-bold sm:inline-block">Natouris</span> */}
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex flex-1 items-center justify-between">
          <ul className="flex gap-6">
            {navLinks.map((link) => (
              <li key={link.name} className="relative group">
                <Link
                  href={link.href}
                  className={`text-sm font-medium transition-colors hover:text-foreground flex items-center ${isActive(link.href) ? "text-foreground" : "text-muted-foreground"
                    }`}
                  aria-expanded={link.dropdown ? "false" : undefined}
                  aria-haspopup={link.dropdown ? "true" : undefined}
                >
                  {link.name}
                  {link.dropdown && <ChevronDown className="ml-1 h-4 w-4" />}
                </Link>

                {link.dropdown && (
                  <div className="absolute left-0 top-full pt-2 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <ul className="bg-card rounded-lg shadow-lg border border-border overflow-hidden">
                      {link.dropdown.map((item) => (
                        <li key={item.name}>
                          <Link
                            href={item.href}
                            className="block px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                          >
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-4">
            {isSearchOpen ? (
              <form onSubmit={handleSearch} className="relative">
                <Input
                  type="search"
                  placeholder="Search..."
                  className="w-[200px] pr-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  onBlur={() => {
                    if (!searchQuery) setIsSearchOpen(false)
                  }}
                />
                <button type="submit" className="absolute right-2 top-2.5">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <span className="sr-only">Search</span>
                </button>
              </form>
            ) : (
              <Button variant="ghost" size="icon" onClick={() => setIsSearchOpen(true)} className="h-10 w-10">
                <Search className="h-5 w-5" />
                <span className="sr-only">Search</span>
              </Button>
            )}
            <ThemeToggle />
          </div>
        </nav>

        {/* Mobile Navigation */}
        <div className="flex flex-1 items-center justify-end md:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsSearchOpen(!isSearchOpen)} className="mr-2 h-10 w-10">
            <Search className="h-5 w-5" />
            <span className="sr-only">Search</span>
          </Button>
          <ThemeToggle />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="ml-2 h-10 w-10"
            aria-expanded={isMenuOpen}
            aria-label="Toggle menu"
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle menu</span>
          </Button>
        </div>

        {/* Mobile Search */}
        {isSearchOpen && (
          <div className="absolute inset-x-0 top-16 p-4 bg-background border-b md:hidden">
            <form onSubmit={handleSearch} className="relative">
              <Input
                type="search"
                placeholder="Search destinations, tips, stories..."
                className="w-full pr-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              <button type="submit" className="absolute right-2 top-2.5">
                <Search className="h-4 w-4 text-muted-foreground" />
                <span className="sr-only">Search</span>
              </button>
            </form>
          </div>
        )}

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="fixed inset-0 top-16 z-50 bg-background md:hidden">
            <div className="container p-6">
              <nav className="flex flex-col gap-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Navigation</h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsMenuOpen(false)}
                    className="h-10 w-10"
                    aria-label="Close menu"
                  >
                    <X className="h-5 w-5" />
                    <span className="sr-only">Close menu</span>
                  </Button>
                </div>
                <ul className="grid gap-4">
                  {navLinks.map((link) => (
                    <li key={link.name}>
                      <div>
                        <Link
                          href={link.href}
                          className={`flex items-center gap-2 text-lg font-medium py-2 ${isActive(link.href) ? "text-primary" : "text-muted-foreground"
                            }`}
                          onClick={() => !link.dropdown && setIsMenuOpen(false)}
                        >
                          {link.name}
                          {link.dropdown && <ChevronDown className="ml-1 h-4 w-4" />}
                        </Link>

                        {link.dropdown && (
                          <ul className="ml-4 mt-1 border-l border-border pl-4">
                            {link.dropdown.map((item) => (
                              <li key={item.name}>
                                <Link
                                  href={item.href}
                                  className="block py-2 text-muted-foreground hover:text-primary"
                                  onClick={() => setIsMenuOpen(false)}
                                >
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}
